import { Menu } from "@headlessui/react";

import DropdownButton from "./DropdownButton";
import LoadingSpinner from "./LoadingSpinner";
import { ScoreRadioGroup } from "./ScoreRadioGroup";
import { Slider } from "./Slider";

import { useUpdateRequestScore } from "@/queries";
import { useAuth } from "@/context/auth-context";

const getCurrentScore = (scoreValue: number): number => {
  if (scoreValue && scoreValue <= 100) {
    return scoreValue;
  }

  if (scoreValue === 0) {
    return 0;
  }

  return 50;
};

const ScoreDisplayText = ({
  loading,
  scores,
  requestScore,
}: {
  loading: boolean;
  scores: Record<string, number>[];
  requestScore: number | null;
}) => {
  if (loading) {
    return <LoadingSpinner size={5} />;
  }

  if (scores.length === 0 && scores !== null) {
    return <>Score {requestScore ?? "N/A"}</>;
  }

  return <>Scores</>;
};

export const ScoreDropdown = ({
  score,
  scores,
  setScore,
  requestId,
  requestScore,
  setResetScore,
  resetScore,
}: {
  score: number[];
  scores: Record<string, number>[];
  setScore: (score: number[]) => void;
  requestId: string;
  requestScore: number | null;
  setResetScore: (reset: boolean) => void;
  resetScore: boolean;
}) => {
  const userToken = useAuth()?.userToken || "";
  const updateRequestScore = useUpdateRequestScore();
  const loading = updateRequestScore.isLoading;

  const currentScore = getCurrentScore(score[0]);

  const handleSubmit = () => {
    updateRequestScore.mutate({
      score: resetScore ? score[0] : currentScore,
      scoreName: "default",
      requestId,
      resetScore,
      userToken,
    });
  };

  return (
    <div className="shrink-0 rounded bg-blue-100 px-2 py-1 text-sm">
      <DropdownButton
        dropdownItems={[]}
        width="w-[22rem]"
        dropdownContent={
          <div className="z-50 flex flex-col space-y-2 px-4 py-3">
            <div>
              <ScoreRadioGroup setResetScore={setResetScore} resetScore={resetScore} />
              <div className="mb-2 mt-4 flex w-full flex-row items-center justify-between">
                <Slider
                  className="w-full"
                  onValueChange={value => setScore([Number(value)])}
                  value={currentScore.toString()}
                  isDisabled={resetScore}
                  hasInput
                  step={1}
                />
              </div>
            </div>
            <Menu.Button
              className="mt-6 w-full rounded bg-blue-500 p-2 text-white hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-gray-300"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <LoadingSpinner size={5} /> : "Apply"}
            </Menu.Button>
            {scores &&
              scores.length > 0 &&
              Object.entries(scores).map(([key, otherScore]) => (
                <div key={key} className="mt-2 rounded bg-gray-100 p-2">
                  {otherScore &&
                    Object.entries(otherScore).map(([name, value]) => (
                      <div
                        className="flex flex-row justify-between text-sm text-gray-600"
                        key={name}
                      >
                        <p>{`${name}`}</p>
                        <p>{`${JSON.stringify(value)}`}</p>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        }
      >
        <div className="inline-flex items-center gap-1 whitespace-nowrap">
          <p className="whitespace-nowrap text-blue-600">
            <ScoreDisplayText
              loading={loading}
              scores={scores}
              requestScore={requestScore ?? null}
            />
          </p>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
            className="ml-1 h-3 w-3 text-blue-600"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </DropdownButton>
    </div>
  );
};
